// WSS client: hello/welcome, pose reporting, snapshot interpolation for remote
// players, event FX (tracers, impacts), live tunable push, dev live-reload.
import * as THREE from 'three';

// a place() with no answer by then counts as unanswered (main.js enterAt retries once)
export const PLACE_TIMEOUT_MS = 5000;

export class Net {
  constructor() {
    this.ws = null;
    this.id = null;
    this.tunables = null;
    this.ice = [];             // RTCIceServer[] from welcome (voice WebRTC)
    this.onWelcome = null;
    this.onTunables = null;
    this.onEvent = null;
    this.onRtc = null;         // voice signaling (client/src/voice.js)
    this.onRemoteGone = null;  // remote entity pruned (voice peer teardown)
    this.onRoster = null;      // (roster) every connected human, when it arrives (~1 Hz)
    this.onPlaced = null;      // (pos, waited) every accepted 'placed' → main.js teleports
    this.onWorld = null;       // ({world, bake, from, from_bake}) a promote kept the socket → main.js swapWorld
    this._placeWaits = [];     // place() resolvers, request order
    this.ents = [];            // latest snapshot entities: PLACED players (incl. self) + bots
    // the latest snapshot `roster` [{id, n, up, in}] — every connection, in
    // the world (in 1) or on the Map before entry (in 0); the server sends it
    // at ~1 Hz, not with every snapshot (PROTOCOL.md `s`)
    this.roster = [];
    this.remotes = new Map();   // id -> {snaps:[], group, parts}
    this.seq = 0;
    this.connected = false;
    this.lk = null;            // welcome's load key: a beaconed `load` report names its connection
  }

  connect(name) {
    return new Promise((resolve, reject) => {
      const url = `wss://${location.host}`;
      const ws = new WebSocket(url);
      this.ws = ws;
      const to = setTimeout(() => reject(new Error('connect timeout')), 8000);
      ws.onopen = () => ws.send(JSON.stringify({ t: 'hello', name, platform: this._platform() }));
      ws.onmessage = (ev) => {
        let m;
        try { m = JSON.parse(ev.data); } catch { return; }
        switch (m.t) {
          case 'welcome':
            clearTimeout(to);
            this.id = m.id;
            this.tunables = m.tunables;
            this.ice = m.ice || [];
            this.lk = typeof m.lk === 'string' ? m.lk : null;
            this.connected = true;
            resolve(m);
            break;
          case 's': this._snapshot(m); break;
          case 'tunables':
            this.tunables = m.data;
            if (this.onTunables) this.onTunables(m.data);
            break;
          case 'rtc': if (this.onRtc) this.onRtc(m); break;
          case 'placed': {
            // GENERIC: every accepted placement moves us (the entry — a
            // Map card's ⤳ Beam —, GPS follow, inspect "beam to there") —
            // main.js onPlaced teleports;
            // the oldest waiting place() then learns the outcome
            // (2nd arg: a place() is still waiting — false = a reply after
            // its timeout, which main.js ignores before entry)
            const pos = Array.isArray(m.pos) ? m.pos : null;
            if (pos && this.onPlaced) {
              try { this.onPlaced(pos, this._placeWaits.length > 0); } catch (e) { console.error('[placed]', e); }
            }
            const w = this._placeWaits.shift();
            if (w) w(pos);
            break;
          }
          // a promote: the server swapped its world and KEPT this socket
          // {world, bake, from, from_bake} -> main.js (worldswap.js swapWorld)
          case 'world': if (this.onWorld) this.onWorld(m); else setTimeout(() => location.reload(), 300); break;
          // dev live-reload: a rebuilt client bundle (gs.js CLIENT_DIR watch)
          case 'reload': setTimeout(() => location.reload(), 300); break;
          case 'announce': console.log('[announce]', m.text); break;
          case 'full':
            clearTimeout(to);
            reject(new Error(m.max ? `world is full (${m.max} player limit)` : 'server full'));
            break;
          default: break;
        }
      };
      ws.onerror = () => { clearTimeout(to); reject(new Error('websocket error')); };
      ws.onclose = () => {
        const was = this.connected;
        this.connected = false;
        if (was && this.onDisconnect) this.onDisconnect();
      };
    });
  }

  _platform() {
    const ua = navigator.userAgent;
    if (/OculusBrowser|Quest/i.test(ua)) return 'quest';
    if (/Mobile/i.test(ua)) return 'mobile';
    return 'desktop';
  }

  sendInput(pos, yaw, state, head, hands, ind = 0) {
    if (!this.connected) return;
    const m = {
      t: 'i', seq: ++this.seq,
      pos: [r2(pos.x), r2(pos.y), r2(pos.z)], yaw: r2(yaw), state, head, hands,
    };
    if (ind) m.ind = ind;   // bike turn indicator (-1/+1), echoed in snapshots
    this.ws.send(JSON.stringify(m));
  }

  fire(weapon, origin, dir) {
    if (!this.connected) return;
    this.ws.send(JSON.stringify({
      t: 'fire', weapon,
      origin: [r2(origin.x), r2(origin.y), r2(origin.z)],
      dir: [r3(dir.x), r3(dir.y), r3(dir.z)],
    }));
  }

  // live rename (landing-page name field); empty → server's player-<id> default
  sendName(name) {
    if (this.connected) this.ws.send(JSON.stringify({ t: 'name', name }));
  }

  // station-zone board/exit teleport (server-gated, see sim.beam)
  sendBeam(to) {
    if (this.connected) {
      this.ws.send(JSON.stringify({ t: 'beam', to: to.map((v) => r2(v)) }));
    }
  }

  // inspect card telemetry (inspect.js): {kind, had, action, dist}, one per
  // card open and one per action — gs.js cleanInspect whitelists it
  inspect(data) {
    if (this.connected) this.ws.send(JSON.stringify({ t: 'inspect', data }));
  }

  // A placement (server-gated, see sim.place; the first one is the entry):
  // resolves to the authoritative [x,y,z] the server seated us at, or null when refused /
  // unanswered — the caller must NOT move the body unless it gets a position,
  // or the movement validator rejects the jump and corr snaps it back.
  // The teleport itself is NOT the caller's: the 'placed' case above hands
  // every accepted pos to onPlaced (main.js) — one handler for every source.
  // Replies come back in request order; a timed-out waiter leaves the queue.
  // `why`: resolve {pos, why} instead — why 'ok' | 'refused' (the server
  // answered no seat) | 'timeout' (no answer in PLACE_TIMEOUT_MS) | 'offline'.
  place(x, z, why = false) {
    const out = (pos, w) => (why ? { pos, why: w } : pos);
    if (!this.connected) return Promise.resolve(out(null, 'offline'));
    return new Promise((resolve) => {
      const w = (pos) => { clearTimeout(to); resolve(out(pos, pos ? 'ok' : 'refused')); };
      const to = setTimeout(() => {
        const i = this._placeWaits.indexOf(w);
        if (i >= 0) this._placeWaits.splice(i, 1);
        resolve(out(null, 'timeout'));
      }, PLACE_TIMEOUT_MS);
      this._placeWaits.push(w);
      this.ws.send(JSON.stringify({ t: 'place', to: [r2(x), r2(z)] }));
    });
  }

  // leave the world (VR session end → the Map before entry): the server
  // un-places us — out of `ents`, no position — until the next place()
  leave() {
    if (this.connected) this.ws.send(JSON.stringify({ t: 'leave' }));
  }

  telemetry(data) {
    if (this.connected) this.ws.send(JSON.stringify({ t: 'telemetry', data }));
  }

  // the load report (main.js buildLoadReport, PROTOCOL.md; one per phase)
  sendLoad(data) {
    if (this.connected) this.ws.send(JSON.stringify({ t: 'load', data }));
  }
  // the same report as a beacon (an abandoned page: the browser delivers it
  // after the page is gone) to the child's POST /report/load, keyed by the
  // welcome's load key; false = not queued (no key, no beacon API, too big)
  beaconLoad(data) {
    if (!this.lk || typeof navigator === 'undefined' || !navigator.sendBeacon) return false;
    try { return navigator.sendBeacon('/report/load', JSON.stringify({ k: this.lk, data })); } catch { return false; }
  }

  // voice signaling relay (see PROTOCOL.md `rtc`): {kind, to?, data?}
  sendRtc(msg) {
    if (this.connected) this.ws.send(JSON.stringify({ t: 'rtc', ...msg }));
  }

  _snapshot(m) {
    const now = performance.now();
    this.ents = m.ents;
    if (Array.isArray(m.roster)) {
      this.roster = m.roster;
      if (this.onRoster) this.onRoster(m.roster);
    }
    if (m.lift && this.onLift) this.onLift(m.lift.s);
    if (m.lift && m.lift.a && this.onAerial) this.onAerial(m.lift.a);
    const seen = new Set();
    for (const e of m.ents) {
      if (!Array.isArray(e.p)) continue;            // (ents are placed-only; never a ghost at 0,0)
      if (e.id === this.id) {
        // the server rejected our reported motion for several frames running —
        // only we can move our own body, so we teleport ourselves back
        if (e.corr && this.onCorrection) this.onCorrection(e.corr);
        continue;
      }
      seen.add(e.id);
      let r = this.remotes.get(e.id);
      if (!r) {
        r = { snaps: [], ent: e, needsAvatar: true, bot: !!e.bot };
        this.remotes.set(e.id, r);
      }
      r.ent = e;
      r.snaps.push({ t: now, p: e.p, yaw: e.yaw, head: e.head, alive: e.alive });
      if (r.snaps.length > 12) r.snaps.shift();
    }
    for (const id of this.remotes.keys()) {
      if (!seen.has(id)) {
        const r = this.remotes.get(id);
        if (r.group && r.group.parent) r.group.parent.remove(r.group);
        this.remotes.delete(id);
        if (this.onRemoteGone) this.onRemoteGone(id);   // voice peer teardown
      }
    }
    if (m.ev && m.ev.length) {
      for (const ev of m.ev) {
        // firing puts that avatar's face into attack mode for a beat
        if (ev.e === 'shot') {
          const rr = this.remotes.get(ev.id);
          if (rr) rr.lastShotAt = now;
        }
        if (this.onEvent) this.onEvent(ev);
      }
    }
  }

  // interpolate remotes into their avatar groups; create avatars lazily.
  // roads (optional road-mask sampler) tells the bike head-shake offroad from road.
  updateRemotes(scene, dt, roads) {
    const delay = (this.tunables && this.tunables.net.interp_delay_ms) || 100;
    const now = performance.now();
    const t = now - delay;
    for (const r of this.remotes.values()) {
      if (r.needsAvatar) {
        r.group = makeAvatar(r.ent.n, r.bot);
        r.parts = r.group.userData;
        r.bobT = 0;
        scene.add(r.group);
        r.needsAvatar = false;
      }
      const s = r.snaps;
      if (!s.length) continue;
      let a = s[0], b = s[s.length - 1];
      for (let i = s.length - 1; i > 0; i--) {
        if (s[i - 1].t <= t) { a = s[i - 1]; b = s[i]; break; }
      }
      const f = b.t > a.t ? Math.min(1, Math.max(0, (t - a.t) / (b.t - a.t))) : 1;
      r.group.position.set(
        a.p[0] + (b.p[0] - a.p[0]) * f,
        a.p[1] + (b.p[1] - a.p[1]) * f,
        a.p[2] + (b.p[2] - a.p[2]) * f,
      );
      r.group.rotation.y = -(a.yaw + (b.yaw - a.yaw) * f);
      r.group.visible = !!r.ent.alive;
      const parts = r.parts;
      // chest nametag follows live renames (snapshot `n` is authoritative)
      if (parts.tagName !== r.ent.n) drawTag(parts, r.ent.n);

      // velocity estimate from the interpolation pair (drives bob/expression)
      const dts = Math.max(0.02, (b.t - a.t) / 1000);
      const vy = (b.p[1] - a.p[1]) / dts;
      const speed = Math.hypot((b.p[0] - a.p[0]) / dts, (b.p[2] - a.p[2]) / dts);
      const st = r.ent.st;

      // head placement: reported height, plus walk bob / offroad-bike shake
      let headY = 1.62, hx = 0, hz = 0;
      if (r.ent.head && r.ent.head.p) {
        headY = Math.max(0.5, r.ent.head.p[1] - r.group.position.y);
      }
      if (st === 'ground' && speed > 0.6) {
        r.bobT += dt * (5 + speed * 1.1);           // step-rate head wobble
        headY += Math.sin(r.bobT) * 0.03;
        hx += Math.sin(r.bobT * 0.5) * 0.012;
      }
      if (st === 'bike' && speed > 2
          && !(roads && roads.at(r.group.position.x, r.group.position.z) > 0.3)) {
        const amp = Math.min(0.05, 0.012 + speed * 0.0012);   // offroad ruts
        hx += (Math.random() - 0.5) * amp;
        headY += (Math.random() - 0.5) * amp * 1.6;
        hz += (Math.random() - 0.5) * amp * 0.6;
      }
      parts.head.position.set(hx, headY, hz);

      // face points where the player actually looks: the reported headset
      // quaternion (world) mapped into this avatar's frame, smoothed. Bots
      // report no head pose, so their face rides the body yaw.
      if (r.ent.head && r.ent.head.q) {
        _hq.fromArray(r.ent.head.q);
        _gq.copy(r.group.quaternion).invert().multiply(_hq);
        parts.head.quaternion.slerp(_gq, Math.min(1, dt * 10));
      }

      // wheels appear under the rider on the bike, spinning with speed
      parts.bike.visible = st === 'bike';
      if (parts.bike.visible) {
        for (const w of parts.wheels) w.rotation.x -= (speed / 0.28) * dt;
      }
      // turn indicator (snapshot `ind`): blink the amber lamps on that side
      const ind = st === 'bike' ? (r.ent.ind || 0) : 0;
      const blinkOn = (now % 900) < 500;
      for (const l of parts.indL) l.visible = ind === -1 && blinkOn;
      for (const l of parts.indR) l.visible = ind === 1 && blinkOn;

      // expression: shooting = angry; bike / jet-suit / fast descent = smile
      const angry = r.lastShotAt && now - r.lastShotAt < 2500;
      const smile = !angry
        && (st === 'bike' || st === 'glide' || (vy < -2 && speed > 4));
      setFace(parts, angry ? 'angry' : smile ? 'smile' : 'neutral');
    }
  }
}

function makeAvatar(name, bot) {
  const g = new THREE.Group();
  const mat = new THREE.MeshLambertMaterial({ color: bot ? 0x35586e : 0xd84f35 });
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.26, 0.9, 3, 8), mat);
  body.position.y = 0.95;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 8),
    new THREE.MeshLambertMaterial({ color: bot ? 0x8fb4c9 : 0xe8c39e }));
  head.position.y = 1.62;
  g.add(body, head);

  // ---- face: every feature is a CHILD OF THE HEAD, so it turns with the
  // reported gaze — other players see where this one is looking. Faces the
  // avatar's -Z (its movement direction) at rest.
  const dark = new THREE.MeshBasicMaterial({ color: 0x201a16 });
  if (bot) {
    const visor = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.05, 0.04),
      new THREE.MeshBasicMaterial({ color: 0xff3b2f }));
    visor.position.set(0, 0.03, -0.14);        // bot "eyes" ride the head now
    head.add(visor);
  } else {
    const white = new THREE.MeshBasicMaterial({ color: 0xf4f4f4 });
    for (const sx of [-1, 1]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.034, 8, 6), white);
      eye.position.set(sx * 0.058, 0.03, -0.135);
      const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.016, 6, 5), dark);
      pupil.position.set(sx * 0.058, 0.03, -0.162);
      head.add(eye, pupil);
    }
  }
  // angry brows (hidden unless attacking): inner tips angled down
  const browGeo = new THREE.BoxGeometry(0.06, 0.014, 0.012);
  const browL = new THREE.Mesh(browGeo, dark);
  browL.position.set(-0.058, 0.085, -0.15);
  browL.rotation.z = -0.5;
  const browR = new THREE.Mesh(browGeo, dark);
  browR.position.set(0.058, 0.085, -0.15);
  browR.rotation.z = 0.5;
  browL.visible = browR.visible = false;
  // three mouths, one visible at a time
  const mouthNeutral = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.013, 0.012), dark);
  mouthNeutral.position.set(0, -0.055, -0.162);
  const arcGeo = new THREE.TorusGeometry(0.042, 0.011, 6, 12, Math.PI);
  const mouthSmile = new THREE.Mesh(arcGeo, dark);   // arc flipped → corners up
  mouthSmile.position.set(0, -0.035, -0.162);
  mouthSmile.rotation.z = Math.PI;
  mouthSmile.visible = false;
  const mouthAngry = new THREE.Mesh(arcGeo, dark);   // frown: corners down
  mouthAngry.position.set(0, -0.085, -0.162);
  mouthAngry.visible = false;
  head.add(browL, browR, mouthNeutral, mouthSmile, mouthAngry);

  // ---- bike: wheels + frame shown only while st === 'bike', so other
  // players can see this one is on wheels
  const bike = new THREE.Group();
  const wheelGeo = new THREE.TorusGeometry(0.28, 0.045, 8, 16);
  wheelGeo.rotateY(Math.PI / 2);                     // axis → X: rolls along Z
  const frameMat = new THREE.MeshLambertMaterial({ color: 0x14161a });
  const wheels = [];
  for (const z of [-0.45, 0.45]) {
    const w = new THREE.Mesh(wheelGeo, frameMat);
    w.position.set(0, 0.28, z);
    bike.add(w);
    wheels.push(w);
  }
  const beam = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.95), frameMat);
  beam.position.y = 0.5;
  const bars = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.04, 0.04), frameMat);
  bars.position.set(0, 0.75, -0.42);
  bike.add(beam, bars);
  // turn indicators: amber lamps on BOTH sides of the ride (front bar end +
  // tail), blinked from the snapshot's `ind` in updateRemotes. Right = +X.
  const indMat = new THREE.MeshBasicMaterial({ color: 0xffa424, toneMapped: false });   // a lamp: full signal colour
  const indL = [], indR = [];
  for (const sx of [-1, 1]) {
    const arr = sx < 0 ? indL : indR;
    for (const [y, z] of [[0.75, -0.45], [0.55, 0.5]]) {
      const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 6), indMat);
      lamp.position.set(sx * 0.26, y, z);
      lamp.visible = false;
      bike.add(lamp);
      arr.push(lamp);
    }
  }
  bike.visible = false;
  g.add(bike);

  // ---- chest nametag: a small canvas plate on the torso front (-Z, same
  // side as the face), so every visible player carries their name
  const tagCv = document.createElement('canvas');
  tagCv.width = 256; tagCv.height = 64;
  const tagTex = new THREE.CanvasTexture(tagCv);
  tagTex.colorSpace = THREE.SRGBColorSpace;
  tagTex.anisotropy = 4;
  const tag = new THREE.Mesh(new THREE.PlaneGeometry(0.5, 0.125),
    new THREE.MeshBasicMaterial({ map: tagTex, transparent: true, toneMapped: false }));   // UI plate
  tag.position.set(0, 1.18, -0.265);
  tag.rotation.y = Math.PI;               // plane's +Z normal → avatar front (-Z)
  g.add(tag);

  g.userData = {
    head, bike, wheels, indL, indR, browL, browR,
    mouthNeutral, mouthSmile, mouthAngry, expr: 'neutral',
    tagCv, tagTex, tagName: null,
  };
  drawTag(g.userData, name);
  g.name = 'avatar:' + name;
  return g;
}

function drawTag(parts, name) {
  parts.tagName = name;
  const cv = parts.tagCv, c = cv.getContext('2d');
  const text = String(name || '').slice(0, 24);
  c.clearRect(0, 0, cv.width, cv.height);
  c.fillStyle = 'rgba(14,16,22,0.78)';
  c.beginPath();
  c.roundRect(2, 2, cv.width - 4, cv.height - 4, 14);
  c.fill();
  c.fillStyle = '#f3f5f9';
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  let fs = 34;
  c.font = `bold ${fs}px system-ui, sans-serif`;
  while (c.measureText(text).width > cv.width - 26 && fs > 14) {
    fs -= 2;
    c.font = `bold ${fs}px system-ui, sans-serif`;
  }
  c.fillText(text, cv.width / 2, cv.height / 2 + 1);
  parts.tagTex.needsUpdate = true;
}

function setFace(parts, expr) {
  if (parts.expr === expr) return;
  parts.expr = expr;
  parts.mouthNeutral.visible = expr === 'neutral';
  parts.mouthSmile.visible = expr === 'smile';
  parts.mouthAngry.visible = expr === 'angry';
  parts.browL.visible = parts.browR.visible = expr === 'angry';
}

const _hq = new THREE.Quaternion();
const _gq = new THREE.Quaternion();

function r2(v) { return Math.round(v * 100) / 100; }
function r3(v) { return Math.round(v * 1000) / 1000; }
